import { easeLinear, geoMercator, geoPath, GeoPermissibleObjects, GeoProjection, scaleThreshold, select, selectAll } from 'd3';
import { flow, max, min, set } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { WorldCountriesList } from '../../constants/worldCountriesList';
import { Continent } from '../../documents/analytics/store';
import styles from './SharedAnalytics.module.scss';

const {
    lightestGrey,
    lightGrey,
    fontFamily,
    primary,
    primaryBorder,
    oceanBlue,
    heatMap0,
    heatMap1,
    heatMap2,
    heatMap3,
    heatMap4,
    heatMap5,
    heatMap6,
    heatMap7,
    heatMap8,
    heatMap9,
    heatMap10
} = styles;

interface MapData extends Continent {
    value: number;
    disabled: boolean;
}

export interface ContinentGraphData extends WorldCountriesList {
    disabled: boolean;
    value: number;
}

interface ContinentMapProps {
    height: number;
    width: number;
    continentGeometry: Continent[];
    graphData: ContinentGraphData[];
    id: string;
    colourRange?: string[];
}

const defaultColourRange = [heatMap0, heatMap1, heatMap2, heatMap3, heatMap4, heatMap5, heatMap6, heatMap7, heatMap8, heatMap9, heatMap10];

export const ContinentMap: React.FC<ContinentMapProps> = ({
    height,
    width,
    continentGeometry,
    graphData,
    id,
    colourRange = defaultColourRange
}) => {
    const svgRef = useRef<SVGSVGElement | null>(null);

    const tooltipContent = useCallback((tooltipValue: string) => `<tspan dy=12 x=10>${tooltipValue}</tspan>`, []);
    const getTooltip = useCallback((name: string, value: number) => `${name}: ${value}`, []);
    const getTooltipWidth = useCallback((tooltipValue: string) => max([(`${tooltipValue}`.length * 8) + 10, 100])!, []);

    const getValue = useCallback((id: string) => graphData.filter(({ continent }) => continent === id).reduce((acc, country) => acc + country.value, 0), [graphData]);
    const getIsDisabled = useCallback((id: string) => graphData.filter(({ continent }) => continent === id).some(({ disabled }) => disabled), [graphData]);

    const data: MapData[] = useMemo(() => continentGeometry.map(continent => flow(
        set('value', getValue(continent.properties.continent)),
        set('disabled', getIsDisabled(continent.properties.continent))
    )(continent)), [getValue, continentGeometry, getIsDisabled]);

    const scale = useMemo(() => max([width / 10, height / 5])!, [height, width]);

    const colourDomain = useMemo(() => {
        const minimum = min(data.map(({ value }) => value))!;
        const maximum = max(data.map(({ value }) => value))!;
        const integer = Math.floor((maximum - minimum) / (colourRange.length - 1)) || 1;
        return Array(colourRange.length).fill(0).map((_, index) => minimum + (integer * index));
    }, [data, colourRange]);

    useEffect(() => {
        const svg = select<SVGSVGElement, unknown>(svgRef.current!);

        const projection: GeoProjection = geoMercator()
            .scale(scale)
            .center([0, 0])
            .translate([width / 2, height * 0.7]);

        const colorScale = scaleThreshold()
            .domain(colourDomain)
            .range(colourRange as unknown as number[]);

        const continentLines = geoPath().projection(projection);

        const map = svg
            .selectAll('path')
            .data(data)
            .enter()
            .append('g')
            .attr('class', 'map')
            .attr('id', d => `${id}-${d.properties.continent}`)
            .attr('overflow', 'hidden');

        map
            .append('path')
            .attr('d', (d: Continent, e) => continentLines(d as GeoPermissibleObjects, e))
            .attr('fill', (d: MapData) => {
                if (d.disabled || d.value === 0) {
                    return lightGrey;
                }
                return colorScale(d.value!);
            })
            .attr('stroke', primaryBorder)
            .attr('id', (d: Continent) => `continent-${d.properties.continent.replace(/[ ,.]/g, '-').toLowerCase()}`)
            .attr('class', 'primary')
            .transition().delay((_, i) => i * 950).duration(250).ease(easeLinear);

        map
            .on('mouseover', (_, d: MapData) => {
                tooltip.attr('display', null);
                selectAll(`#continent-${d.properties.continent.replace(/[ ,.]/g, '-').toLowerCase()}`)
                    .transition()
                    .duration(10)
                    .attr('opacity', 0.9)
                    .attr('stroke', primary);
            })
            .on('mouseout', (_, d: MapData) => {
                tooltip.attr('display', 'none');
                selectAll(`#continent-${d.properties.continent.replace(/[ ,.]/g, '-').toLowerCase()}`)
                    .transition()
                    .duration(10)
                    .attr('opacity', 1)
                    .attr('stroke', primaryBorder);
            })
            .on('mousemove', (_, d: MapData) => {
                const tooltipValue = getTooltip(d.properties.continent, d.value!);
                tooltip.select('rect')
                    .attr('height', 25)
                    .attr('width', getTooltipWidth(tooltipValue));
                tooltip.select('text')
                    .html(tooltipContent(tooltipValue));
            });

        const tooltip = svg
            .append('g')
            .attr('class', 'tooltip')
            .attr('data-testid', 'opinions-world-map-tooltip')
            .attr('display', 'none')
            .attr('transform', `translate(0, ${height - 25})`);

        tooltip
            .append('rect')
            .attr('rx', 5)
            .attr('fill', lightestGrey);

        tooltip
            .append('text')
            .attr('x', 10)
            .attr('y', 4)
            .attr('fill', primary)
            .attr('text-anchor', 'start')
            .attr('font-size', '14px')
            .attr('font-family', fontFamily)
            .attr('font-weight', 600);

        return () => {
            // Anything added to the svg on mount via .append must be removed on unmount to stop new elements being added each render
            svg.selectAll('.tooltip').remove();
            svg.selectAll('.map').remove();
        };

    }, [height, width, tooltipContent, getTooltip, getTooltipWidth, data, scale, id, colourRange, colourDomain]);

    return (
        <div className={styles.continentChart}>
            <div className={styles.svgWrapper} data-testid={`continents-map-wrapper-${id}`} style={{ height, width }}>
                <svg ref={svgRef} style={{ height, width, overflow: 'hidden', borderRadius: '5px', cursor: 'pointer', backgroundColor: oceanBlue }} />
            </div>
        </div>
    );
};
